import { Fragment } from "react";
import { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import { Link } from "react-router-dom";
import { FiBookOpen, FiTrendingUp, FiUsers, FiArchive } from "react-icons/fi";
import Card from "../../components/Card";
import Loader from "../../components/Loader";
import MostReadList from "../../components/MostReadList";
import Table, { Td, Th } from "../../components/Table";
import service from "../../service";

const parsingChart = (data) => {
  const label = [];
  const value = [];
  for (let key in data) {
    label.push(key);
    value.push(data[key]);
  }

  return {
    label,
    value,
  };
};

const StatCard = ({ icon, label, value, color = "primary" }) => {
  const Icon = icon;
  const colorClassName = {
    primary: "bg-primary-50 text-primary-600",
    green: "bg-green-50 text-green-300",
    yellow: "bg-yellow-50 text-yellow-300",
    red: "bg-red-50 text-red-300",
  }[color];
  return (
    <Card className="flex items-center">
      <div
        className={`w-12 h-12 rounded-lg flex items-center justify-center text-xl mr-4 ${colorClassName}`}
      >
        <Icon />
      </div>
      <div>
        <div className="text-2xl font-bold text-black-100 leading-none">
          {value}
        </div>
        <div className="text-sm text-black-300 mt-1">{label}</div>
      </div>
    </Card>
  );
};

export default function MainStat() {
  const [dataUserLogin, setDataUserLogin] = useState({});
  const [dataMostActiveUser, setDataMostActiveUser] = useState([]);
  const [dataMostReadBook, setDataMostReadBook] = useState([]);
  const [dataMostCategory, setDataMostCategory] = useState([]);
  const [loading, setLoading] = useState(true);

  const getData = async (cancelledRef) => {
    let getUserLogin, getMostActiveUser, getMostReadBook, getMostCategory;
    try {
      getUserLogin = (await service.get("/api/stat/daily-user-login")).data;
      getMostActiveUser = (await service.get("/api/stat/monthly-active-user"))
        .data;
      getMostReadBook = (await service.get("/api/stat/most-read-book")).data;
      getMostCategory = (await service.get("/api/stat/most-read-category"))
        .data;
    } catch (e) {
      if (!cancelledRef.current) {
        setLoading(false);
      }
      return;
    }

    if (cancelledRef.current) return;

    setDataUserLogin(parsingChart(getUserLogin));
    setDataMostActiveUser(getMostActiveUser.data || getMostActiveUser);
    setDataMostReadBook(getMostReadBook.data || getMostReadBook);
    setDataMostCategory(getMostCategory.data || getMostCategory);

    setLoading(false);
  };

  useEffect(() => {
    const cancelledRef = { current: false };
    getData(cancelledRef);
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="h-64 flex justify-center items-center flex-col">
        <Loader className="bg-black-100" />
        <span className="mt-3 text-black-300 text-sm">Memuat</span>
      </div>
    );
  }

  const totalLoginRecent = (dataUserLogin.value || []).reduce(
    (sum, v) => sum + (Number(v) || 0),
    0
  );
  const totalActiveUsers = dataMostActiveUser.length;
  const totalReadBook = dataMostReadBook.reduce(
    (sum, v) => sum + (Number(v.read || v.count) || 0),
    0
  );
  const totalReadCategory = dataMostCategory.reduce(
    (sum, v) => sum + (Number(v.read || v.count) || 0),
    0
  );

  return (
    <Fragment>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <StatCard
          icon={FiTrendingUp}
          label="Login 9 Hari Terakhir"
          value={totalLoginRecent}
          color="primary"
        />
        <StatCard
          icon={FiUsers}
          label="Pengguna Aktif"
          value={totalActiveUsers}
          color="green"
        />
        <StatCard
          icon={FiBookOpen}
          label="Total Baca Buku"
          value={totalReadBook}
          color="yellow"
        />
        <StatCard
          icon={FiArchive}
          label="Total Baca Kategori"
          value={totalReadCategory}
          color="red"
        />
      </div>
      <Card>
        <h2 className="font-bold text-lg mr-auto text-black-200 mb-5">
          Tren Login Pengguna
        </h2>
        <Line
          data={{
            labels: dataUserLogin.label,
            datasets: [
              {
                label: "Jumlah User Login",
                data: dataUserLogin.value,
                fill: true,
                backgroundColor: "rgba(37, 99, 235, 0.08)",
                borderColor: "#2563EB",
                pointBackgroundColor: "#2563EB",
                pointBorderColor: "#FFFFFF",
                pointBorderWidth: 2,
                pointRadius: 4,
                tension: 0.35,
              },
            ],
          }}
          options={{
            plugins: {
              legend: { display: false },
            },
            scales: {
              y: {
                beginAtZero: true,
                grid: { color: "#F1F5F9" },
              },
              x: {
                grid: { display: false },
              },
            },
          }}
          className="h-full"
          width={320}
        />
      </Card>
      <div className="flex flex-col lg:flex-row mt-5">
        <div className="flex-1 mr-0 mb-5 lg:mr-8 lg:mb-0">
          <Card>
            <h2 className="font-bold text-lg mr-auto text-black-200 mb-5">
              Pengguna Aktif Bulan Ini
            </h2>
            <Table>
              <thead>
                <tr>
                  <Th>NO</Th>
                  <Th>NAMA</Th>
                  <Th>TOTAL</Th>
                </tr>
              </thead>
              <tbody>
                {dataMostActiveUser.map((value, index) => (
                  <tr key={`act-${index}`}>
                    <Td className="text-center">{index + 1}</Td>
                    <Td>
                      <Link
                        to={`/read-logs/${value.id}`}
                        className="text-primary-600 font-medium hover:underline"
                      >
                        {value.fullname}
                      </Link>
                    </Td>
                    <Td className="text-center">{value.total}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </div>
        <div className="w-full lg:w-1/3">
          <Card className="mb-5">
            <h2 className="font-bold text-lg mr-auto text-black-200 mb-5">
              Buku Teratas
            </h2>
            {dataMostReadBook.map((value, index) => (
              <MostReadList
                title={value.title}
                total={value.read || 0}
                key={`rb-${index}`}
              />
            ))}
          </Card>
          <Card>
            <h2 className="font-bold text-lg mr-auto text-black-200 mb-5">
              Kategori Teratas
            </h2>
            {dataMostCategory.map((value, index) => (
              <MostReadList
                title={value.name}
                total={value.read || 0}
                key={`c-${index}`}
              />
            ))}
          </Card>
        </div>
      </div>
    </Fragment>
  );
}
